<template>
  <section
    class="pdf-learning-viewer"
    :class="{ fullscreen: isFullscreen, mobile: isMobile, 'readonly': readOnly }"
    dir="ltr"
    @contextmenu.prevent
  >
    <header class="pdf-toolbar" :class="{ collapsed: mobileToolbarCollapsed }">
      <div class="toolbar-row main-row">
        <button class="icon-btn mobile-only" type="button" @click="mobileToolbarCollapsed = !mobileToolbarCollapsed">
          <i class="pi pi-bars"></i>
        </button>
        <button class="icon-btn" type="button" @click="goToPage(currentPage - 1)" :disabled="currentPage <= 1">
          <i class="pi pi-chevron-left"></i>
        </button>
        <label class="page-control">
          <input v-model.number="pageInput" type="number" min="1" :max="totalPages" @change="goToPage(pageInput)" />
          <span>/ {{ totalPages || '-' }}</span>
        </label>
        <button class="icon-btn" type="button" @click="goToPage(currentPage + 1)" :disabled="currentPage >= totalPages">
          <i class="pi pi-chevron-right"></i>
        </button>
        <div class="toolbar-spacer"></div>
        <span class="save-state" :class="saveState">{{ saveText }}</span>
        <button class="icon-btn" type="button" @click="toggleFullscreen">
          <i :class="['pi', isFullscreen ? 'pi-window-minimize' : 'pi-window-maximize']"></i>
        </button>
      </div>

      <div class="toolbar-row secondary-row">
        <button class="icon-btn" type="button" @click="setZoom(zoom - 0.15)"><i class="pi pi-search-minus"></i></button>
        <span class="zoom-label">{{ Math.round(zoom * 100) }}%</span>
        <button class="icon-btn" type="button" @click="setZoom(zoom + 0.15)"><i class="pi pi-search-plus"></i></button>
        <button class="text-btn" type="button" @click="fitWidth">Fit Width</button>
        <button class="text-btn" type="button" @click="fitPage">Fit Page</button>
        <button class="icon-btn" type="button" @click="rotatePage"><i class="pi pi-refresh"></i></button>
        <div class="search-box">
          <i class="pi pi-search"></i>
          <input v-model.trim="searchQuery" type="search" placeholder="Search" @keyup.enter="runSearch" />
          <button class="mini-btn" type="button" @click="runSearch">Go</button>
          <span v-if="searchResults.length">{{ activeSearchIndex + 1 }}/{{ searchResults.length }}</span>
        </div>
      </div>
    </header>

    <div class="pdf-body">
      <aside class="thumbs-panel" :class="{ open: thumbnailsOpen }">
        <button class="thumb-toggle mobile-only" type="button" @click="thumbnailsOpen = false">
          <i class="pi pi-times"></i>
        </button>
        <div
          v-for="page in pageNumbers"
          :key="'thumb-' + page"
          class="thumb"
          :class="{ active: page === currentPage }"
          :data-thumb-page="page"
          @click="goToPage(page)"
        >
          <canvas :ref="el => setThumbRef(page, el)"></canvas>
          <span>{{ page }}</span>
        </div>
      </aside>

      <main
        ref="scrollArea"
        class="pdf-scroll"
        @scroll="onScroll"
        @wheel="onWheel"
        @touchstart.passive="onTouchStart"
        @touchend.passive="onTouchEnd"
      >
        <button class="floating-thumbs mobile-only" type="button" @click="thumbnailsOpen = true">
          <i class="pi pi-images"></i>
        </button>
        <div v-if="loading" class="viewer-loading">
          <span class="loader-dot"></span>
          Loading PDF...
        </div>
        <div v-if="error" class="viewer-error">{{ error }}</div>

        <div class="virtual-spacer" :style="{ height: topSpacerHeight + 'px' }"></div>
        <div
          v-for="page in visiblePageNumbers"
          :key="'page-' + page"
          class="page-shell"
          :data-page="page"
          :style="{ height: pageHeights[page] + 'px' }"
        >
          <div
            class="page-frame"
            :style="pageFrameStyle(page)"
          >
            <canvas :ref="el => setCanvasRef(page, el)" class="pdf-canvas"></canvas>
            <svg
              class="annotation-layer"
              :viewBox="'0 0 1 1'"
              preserveAspectRatio="none"
              @pointerdown="onAnnotationPointerDown($event, page)"
              @pointermove="onAnnotationPointerMove"
              @pointerup="onAnnotationPointerUp"
              @pointerleave="onAnnotationPointerUp"
            >
              <g v-if="watermarkEnabled && watermarkDisplayText" class="watermark-layer">
                <template v-for="row in watermarkGrid" :key="'wm-row-' + row.y">
                  <text
                    v-for="col in row.cols"
                    :key="'wm-' + row.y + '-' + col.x"
                    :x="col.x"
                    :y="row.y"
                    transform="rotate(-28)"
                    fill="currentColor"
                  >{{ watermarkDisplayText }}</text>
                </template>
              </g>
              <rect
                v-for="(hit, index) in searchHighlights[page] || []"
                :key="'hit-' + page + '-' + index"
                class="search-highlight"
                :x="hit.x"
                :y="hit.y"
                :width="hit.width"
                :height="hit.height"
              />
              <g v-for="item in annotationsByPage[page] || []" :key="item.id" :class="'annotation-' + item.type">
                <polyline
                  v-if="item.type === 'pen' || item.type === 'highlighter'"
                  :points="pointsToString(item.points)"
                  :stroke="item.color"
                  :stroke-width="item.width"
                  :opacity="item.opacity"
                  fill="none"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  vector-effect="non-scaling-stroke"
                />
                <rect
                  v-else-if="item.type === 'rectangle'"
                  :x="rectFromItem(item).x"
                  :y="rectFromItem(item).y"
                  :width="rectFromItem(item).width"
                  :height="rectFromItem(item).height"
                  :stroke="item.color"
                  stroke-width="2"
                  fill="transparent"
                  vector-effect="non-scaling-stroke"
                />
                <ellipse
                  v-else-if="item.type === 'circle'"
                  :cx="ellipseFromItem(item).cx"
                  :cy="ellipseFromItem(item).cy"
                  :rx="ellipseFromItem(item).rx"
                  :ry="ellipseFromItem(item).ry"
                  :stroke="item.color"
                  stroke-width="2"
                  fill="transparent"
                  vector-effect="non-scaling-stroke"
                />
                <g v-else-if="item.type === 'arrow'">
                  <line
                    :x1="item.start.x"
                    :y1="item.start.y"
                    :x2="item.end.x"
                    :y2="item.end.y"
                    :stroke="item.color"
                    stroke-width="2"
                    vector-effect="non-scaling-stroke"
                  />
                  <polygon :points="arrowHead(item)" :fill="item.color" />
                </g>
                <text
                  v-else-if="item.type === 'text'"
                  :x="item.x"
                  :y="item.y"
                  :fill="item.color"
                  :font-size="item.size"
                  font-family="Arial, sans-serif"
                >{{ item.text }}</text>
              </g>
            </svg>
            <div v-if="renderingPages[page]" class="page-loading">Rendering...</div>
          </div>
        </div>
        <div class="virtual-spacer" :style="{ height: bottomSpacerHeight + 'px' }"></div>
      </main>

      <aside v-if="!readOnly" class="annotation-tools" :class="{ open: toolsOpen }">
        <button class="tool-toggle mobile-only" type="button" @click="toolsOpen = !toolsOpen">
          <i class="pi pi-pencil"></i>
        </button>
        <div class="tool-list">
          <button v-for="tool in tools" :key="tool.name" type="button" :class="{ active: activeTool === tool.name }" @click="selectTool(tool.name)">
            <i :class="tool.icon"></i>
          </button>
          <input v-model="strokeColor" class="color-input" type="color" />
          <button type="button" @click="undo"><i class="pi pi-undo"></i></button>
          <button type="button" @click="redo"><i class="pi pi-refresh"></i></button>
          <button type="button" @click="clearPage"><i class="pi pi-trash"></i></button>
        </div>
      </aside>
    </div>
  </section>
</template>

<script>
import axios from "axios";
import { markRaw } from "vue";
import * as pdfjsLib from "pdfjs-dist/legacy/build/pdf";
import "pdfjs-dist/legacy/build/pdf.worker.entry";

export default {
  name: "PdfLearningViewer",
  props: {
    pdfUrl: { type: String, required: true },
    contentId: { type: [Number, String], required: true },
    readOnly: { type: Boolean, default: false },
    watermarkEnabled: { type: Boolean, default: false },
    watermarkLines: { type: Array, default: () => [] },
  },
  data() {
    return {
      pdf: null,
      totalPages: 0,
      currentPage: 1,
      pageInput: 1,
      zoom: 1,
      rotation: 0,
      pageSizes: {},
      pageHeights: {},
      visiblePages: new Set(),
      renderedPages: new Set(),
      renderingPages: {},
      pendingRenderPages: new Set(),
      renderEpoch: 0,
      pageCanvases: {},
      thumbCanvases: {},
      renderedThumbs: new Set(),
      annotationsByPage: {},
      undoStack: {},
      redoStack: {},
      dirtyPages: new Set(),
      activeTool: "pan",
      strokeColor: "#d93025",
      drawing: null,
      loading: true,
      error: "",
      isFullscreen: false,
      isMobile: window.innerWidth <= 768,
      mobileToolbarCollapsed: window.innerWidth <= 768,
      thumbnailsOpen: false,
      toolsOpen: false,
      searchQuery: "",
      searchResults: [],
      activeSearchIndex: -1,
      searchHighlights: {},
      saveState: "saved",
      autosaveTimer: null,
      progressTimer: null,
      observer: null,
      thumbObserver: null,
      scrollRestore: null,
      renderWindowStart: 1,
      renderWindowEnd: 1,
      renderBuffer: 4,
      progressDirty: false,
      loadingProgress: false,
      lastSavedProgress: null,
      touchStart: null,
      tools: [
        { name: "pan", icon: "pi pi-arrows-alt" },
        { name: "pen", icon: "pi pi-pencil" },
        { name: "highlighter", icon: "pi pi-sun" },
        { name: "eraser", icon: "pi pi-eraser" },
        { name: "text", icon: "pi pi-font" },
        { name: "rectangle", icon: "pi pi-stop" },
        { name: "circle", icon: "pi pi-circle" },
        { name: "arrow", icon: "pi pi-arrow-up-right" },
      ],
    };
  },
  computed: {
    pageNumbers() {
      return Array.from({ length: this.totalPages }, (_, index) => index + 1);
    },
    visiblePageNumbers() {
      const start = Math.max(1, this.renderWindowStart);
      const end = Math.min(this.totalPages, this.renderWindowEnd);
      return Array.from({ length: Math.max(0, end - start + 1) }, (_, index) => start + index);
    },
    topSpacerHeight() {
      return this.sumPageHeights(1, this.renderWindowStart - 1);
    },
    bottomSpacerHeight() {
      return this.sumPageHeights(this.renderWindowEnd + 1, this.totalPages);
    },
    saveText() {
      if (this.saveState === "saving") return "Saving";
      if (this.saveState === "offline") return "Offline saved";
      if (this.saveState === "error") return "Retrying";
      return "Saved";
    },
    authHeaders() {
      const token = localStorage.getItem("token");
      return token ? { Authorization: `Bearer ${token}` } : {};
    },
    offlineKey() {
      return `pdf-reader-offline-${this.contentId}`;
    },
    watermarkDisplayText() {
      return this.watermarkLines.find((line) => !!line) || "";
    },
    watermarkGrid() {
      return [0.18, 0.42, 0.66, 0.9].map((y) => ({
        y,
        cols: [0.05, 0.38, 0.71].map((x) => ({ x })),
      }));
    },
  },
  watch: {
    pdfUrl() {
      this.resetViewer();
      this.loadPdf();
    },
    zoom() {
      this.renderEpoch += 1;
      this.recalculateHeights();
      if (!this.loadingProgress) this.markProgressDirty();
      this.rerenderVisiblePages();
    },
    rotation() {
      this.renderEpoch += 1;
      this.recalculateHeights();
      this.rerenderVisiblePages();
    },
    toolsOpen() {
      this.$nextTick(this.updateMobileFloatingLayout);
    },
  },
  mounted() {
    this.loadPdf();
    this.autosaveTimer = setInterval(this.flushAnnotations, 4000);
    this.progressTimer = setInterval(() => this.saveProgress(), 5000);
    this._resizeHandler = () => {
      this.isMobile = window.innerWidth <= 768;
      this.updateMobileFloatingLayout();
      this.recalculateHeights();
      this.rerenderVisiblePages();
    };
    this._keyHandler = this.onKeydown;
    this._fullscreenHandler = this.onFullscreenChange;
    window.addEventListener("resize", this._resizeHandler);
    window.visualViewport?.addEventListener("resize", this.updateMobileFloatingLayout);
    window.visualViewport?.addEventListener("scroll", this.updateMobileFloatingLayout);
    window.addEventListener("keydown", this._keyHandler);
    document.addEventListener("fullscreenchange", this._fullscreenHandler);
    window.addEventListener("online", this.flushOfflineQueue);
    window.addEventListener("beforeunload", this.saveLocalBackup);
    this.$nextTick(this.updateMobileFloatingLayout);
  },
  beforeUnmount() {
    this.flushAnnotations();
    this.saveProgress(true);
    this.exitFullscreenClass();
    clearInterval(this.autosaveTimer);
    clearInterval(this.progressTimer);
    window.removeEventListener("resize", this._resizeHandler);
    window.visualViewport?.removeEventListener("resize", this.updateMobileFloatingLayout);
    window.visualViewport?.removeEventListener("scroll", this.updateMobileFloatingLayout);
    window.removeEventListener("keydown", this._keyHandler);
    document.removeEventListener("fullscreenchange", this._fullscreenHandler);
    window.removeEventListener("online", this.flushOfflineQueue);
    window.removeEventListener("beforeunload", this.saveLocalBackup);
    if (this.observer) this.observer.disconnect();
    if (this.thumbObserver) this.thumbObserver.disconnect();
    this.destroyAllCanvases();
  },
  methods: {
    selectTool(toolName) {
      this.activeTool = toolName;
      if (this.isMobile && toolName !== "pan") this.toolsOpen = false;
    },
    updateMobileFloatingLayout() {
      if (!this.$el || window.innerWidth > 768) return;
      const viewportHeight = window.visualViewport?.height || window.innerHeight;
      const bottomNav = document.querySelector(".app-bnav");
      const navRect = bottomNav?.getBoundingClientRect();
      const navHeight = navRect ? Math.max(0, window.innerHeight - navRect.top) : 0;
      const safeBottom = Math.max(16, navHeight + 16);
      const maxSheetHeight = Math.max(144, Math.min(220, viewportHeight - safeBottom - 92));
      this.$el.style.setProperty("--pdf-mobile-safe-bottom", `${safeBottom}px`);
      this.$el.style.setProperty("--pdf-mobile-tool-sheet-max-height", `${maxSheetHeight}px`);
    },
    async loadPdf() {
      try {
        this.loading = true;
        this.error = "";
        const task = pdfjsLib.getDocument({
          url: this.pdfUrl,
          httpHeaders: this.authHeaders,
          withCredentials: false,
          disableAutoFetch: true,
          disableStream: false,
        });
        this.pdf = markRaw(await task.promise);
        this.totalPages = this.pdf.numPages;
        this.currentPage = 1;
        this.pageInput = 1;
        await this.preparePageSizes();
        await Promise.all([this.loadAnnotations(), this.loadProgress()]);
        this.$nextTick(() => {
          this.setupObservers();
          this.restoreScrollPosition();
          this.flushOfflineQueue();
        });
      } catch (error) {
        this.error = "Unable to open this PDF.";
      } finally {
        this.loading = false;
      }
    },
    resetViewer() {
      this.destroyAllCanvases();
      this.pdf = null;
      this.totalPages = 0;
      this.currentPage = 1;
      this.pageInput = 1;
      this.renderWindowStart = 1;
      this.renderWindowEnd = 1;
      this.pageSizes = {};
      this.pageHeights = {};
      this.annotationsByPage = {};
      this.searchHighlights = {};
      this.searchResults = [];
      this.renderedPages = new Set();
      this.renderedThumbs = new Set();
      this.pendingRenderPages = new Set();
      this.renderEpoch += 1;
    },
    async preparePageSizes() {
      const sampleLimit = Math.min(this.totalPages, 8);
      let averageRatio = 1.35;
      for (let pageNumber = 1; pageNumber <= sampleLimit; pageNumber++) {
        const page = await this.pdf.getPage(pageNumber);
        const viewport = page.getViewport({ scale: 1, rotation: this.rotation });
        this.pageSizes[pageNumber] = { width: viewport.width, height: viewport.height };
        averageRatio = viewport.height / viewport.width;
      }
      for (let pageNumber = 1; pageNumber <= this.totalPages; pageNumber++) {
        if (!this.pageSizes[pageNumber]) this.pageSizes[pageNumber] = { width: 612, height: 612 * averageRatio };
      }
      this.recalculateHeights();
      this.renderWindowEnd = Math.min(this.totalPages, 12);
    },
    sumPageHeights(start, end) {
      if (!this.totalPages || end < start) return 0;
      let total = 0;
      for (let page = Math.max(1, start); page <= Math.min(this.totalPages, end); page++) {
        total += this.pageHeights[page] || 820;
      }
      return total;
    },
    pageFromScroll(scrollTop) {
      let total = 0;
      for (let page = 1; page <= this.totalPages; page++) {
        total += this.pageHeights[page] || 820;
        if (total >= scrollTop) return page;
      }
      return this.totalPages || 1;
    },
    updateRenderWindowAround(page) {
      const start = Math.max(1, page - this.renderBuffer);
      const end = Math.min(this.totalPages, page + this.renderBuffer + 6);
      if (start === this.renderWindowStart && end === this.renderWindowEnd) return;
      this.renderWindowStart = start;
      this.renderWindowEnd = end;
      this.$nextTick(() => {
        this.setupObservers();
        this.visiblePageNumbers.forEach((pageNumber) => this.renderPage(pageNumber));
        this.cleanupFarPages();
      });
    },
    recalculateHeights() {
      this.pageNumbers.forEach((page) => {
        const size = this.pageSizes[page] || { width: 612, height: 792 };
        const width = size.width * this.zoom;
        const scale = width / size.width;
        this.pageHeights[page] = Math.ceil(size.height * scale + 24);
      });
    },
    pageFrameStyle(page) {
      const size = this.pageSizes[page] || { width: 612, height: 792 };
      const height = Math.max((this.pageHeights[page] || 820) - 24, 200);
      const width = height * (size.width / size.height);
      return { width: `${width}px`, height: `${height}px` };
    },
    setupObservers() {
      if (this.observer) this.observer.disconnect();
      if (this.thumbObserver) this.thumbObserver.disconnect();
      this.observer = new IntersectionObserver(this.onPagesIntersect, {
        root: this.$refs.scrollArea,
        rootMargin: "900px 0px",
        threshold: 0.01,
      });
      this.thumbObserver = new IntersectionObserver(this.onThumbsIntersect, {
        root: this.$el.querySelector(".thumbs-panel"),
        rootMargin: "500px 0px",
        threshold: 0.01,
      });
      this.$el.querySelectorAll(".page-shell").forEach((el) => this.observer.observe(el));
      this.$el.querySelectorAll(".thumb").forEach((el) => this.thumbObserver.observe(el));
    },
    onPagesIntersect(entries) {
      entries.forEach((entry) => {
        const page = Number(entry.target.dataset.page);
        if (entry.isIntersecting) {
          this.visiblePages.add(page);
          this.renderPage(page);
        } else {
          this.visiblePages.delete(page);
        }
      });
      this.updateCurrentPage();
      this.cleanupFarPages();
    },
    onThumbsIntersect(entries) {
      entries.forEach((entry) => {
        if (entry.isIntersecting) this.renderThumbnail(Number(entry.target.dataset.thumbPage));
      });
    },
    setCanvasRef(page, el) {
      if (el) this.pageCanvases[page] = el;
    },
    setThumbRef(page, el) {
      if (el) this.thumbCanvases[page] = el;
    },
    async renderPage(pageNumber) {
      if (!this.pdf) return;
      if (this.renderingPages[pageNumber]) {
        this.pendingRenderPages.add(pageNumber);
        return;
      }
      const canvas = this.pageCanvases[pageNumber];
      if (!canvas) return;
      this.renderingPages[pageNumber] = true;
      const renderEpoch = this.renderEpoch;
      try {
        const page = await this.pdf.getPage(pageNumber);
        const frame = canvas.parentElement;
        const viewportBase = page.getViewport({ scale: 1, rotation: this.rotation });
        this.pageSizes[pageNumber] = { width: viewportBase.width, height: viewportBase.height };
        const scale = frame.clientWidth / viewportBase.width;
        const viewport = page.getViewport({ scale, rotation: this.rotation });
        const dpr = window.devicePixelRatio || 1;
        canvas.width = Math.floor(viewport.width * dpr);
        canvas.height = Math.floor(viewport.height * dpr);
        canvas.style.width = `${viewport.width}px`;
        canvas.style.height = `${viewport.height}px`;
        const context = canvas.getContext("2d", { alpha: false });
        context.setTransform(dpr, 0, 0, dpr, 0, 0);
        await page.render({ canvasContext: context, viewport }).promise;
        this.renderedPages.add(pageNumber);
      } catch (e) {
        this.error = "A page failed to render.";
      } finally {
        this.renderingPages[pageNumber] = false;
        if (this.pendingRenderPages.has(pageNumber) || renderEpoch !== this.renderEpoch) {
          this.pendingRenderPages.delete(pageNumber);
          this.$nextTick(() => this.renderPage(pageNumber));
        }
      }
    },
    async renderThumbnail(pageNumber) {
      if (!this.pdf || this.renderedThumbs.has(pageNumber)) return;
      const canvas = this.thumbCanvases[pageNumber];
      if (!canvas) return;
      const page = await this.pdf.getPage(pageNumber);
      const viewport = page.getViewport({ scale: 0.18, rotation: this.rotation });
      canvas.width = viewport.width;
      canvas.height = viewport.height;
      await page.render({ canvasContext: canvas.getContext("2d"), viewport }).promise;
      this.renderedThumbs.add(pageNumber);
    },
    cleanupFarPages() {
      const keep = new Set();
      this.visiblePages.forEach((page) => {
        for (let i = page - 2; i <= page + 2; i++) if (i >= 1 && i <= this.totalPages) keep.add(i);
      });
      this.renderedPages.forEach((page) => {
        if (!keep.has(page)) this.destroyPageCanvas(page);
      });
    },
    destroyPageCanvas(page) {
      const canvas = this.pageCanvases[page];
      if (canvas) {
        const ctx = canvas.getContext("2d");
        if (ctx) ctx.clearRect(0, 0, canvas.width, canvas.height);
        canvas.width = 0;
        canvas.height = 0;
      }
      this.renderedPages.delete(page);
    },
    destroyAllCanvases() {
      Object.keys(this.pageCanvases).forEach((page) => this.destroyPageCanvas(Number(page)));
      Object.values(this.thumbCanvases).forEach((canvas) => {
        if (canvas) {
          canvas.width = 0;
          canvas.height = 0;
        }
      });
    },
    rerenderVisiblePages() {
      const pages = Array.from(this.visiblePages);
      pages.forEach((page) => this.destroyPageCanvas(page));
      this.$nextTick(() => pages.forEach((page) => this.renderPage(page)));
    },
    goToPage(page) {
      const target = Math.min(Math.max(Number(page) || 1, 1), this.totalPages || 1);
      this.updateRenderWindowAround(target);
      this.$nextTick(() => {
        if (this.$refs.scrollArea) {
          this.$refs.scrollArea.scrollTo({ top: this.sumPageHeights(1, target - 1), behavior: "smooth" });
        }
      });
      this.currentPage = target;
      this.pageInput = target;
      this.markProgressDirty();
    },
    onScroll() {
      this.updateRenderWindowAround(this.pageFromScroll(this.$refs.scrollArea?.scrollTop || 0));
      this.updateCurrentPage();
      this.markProgressDirty();
    },
    onWheel(event) {
      if (!event.ctrlKey) return;
      event.preventDefault();
      this.setZoom(this.zoom + (event.deltaY < 0 ? 0.12 : -0.12));
    },
    onTouchStart(event) {
      const touch = event.changedTouches && event.changedTouches[0];
      if (!touch) return;
      this.touchStart = { x: touch.clientX, y: touch.clientY, time: Date.now() };
    },
    onTouchEnd(event) {
      const touch = event.changedTouches && event.changedTouches[0];
      if (!touch || !this.touchStart) return;
      const dx = touch.clientX - this.touchStart.x;
      const dy = touch.clientY - this.touchStart.y;
      const quick = Date.now() - this.touchStart.time < 500;
      if (quick && Math.abs(dx) > 70 && Math.abs(dx) > Math.abs(dy) * 1.8) {
        this.goToPage(this.currentPage + (dx < 0 ? 1 : -1));
      }
      this.touchStart = null;
    },
    updateCurrentPage() {
      const area = this.$refs.scrollArea;
      if (!area) return;
      const page = this.pageFromScroll(area.scrollTop + 24);
      this.currentPage = page;
      this.pageInput = page;
    },
    setZoom(value) {
      this.applyZoom(value);
    },
    fitWidth() {
      const area = this.$refs.scrollArea;
      const size = this.pageSizes[this.currentPage] || { width: 612, height: 792 };
      const availableWidth = Math.max((area?.clientWidth || window.innerWidth) - (this.isMobile ? 28 : 64), 280);
      this.applyZoom(availableWidth / size.width);
    },
    fitPage() {
      const area = this.$refs.scrollArea;
      const size = this.pageSizes[this.currentPage] || { width: 612, height: 792 };
      const availableWidth = Math.max((area?.clientWidth || window.innerWidth) - (this.isMobile ? 28 : 64), 280);
      const availableHeight = Math.max((area?.clientHeight || window.innerHeight) - 40, 240);
      this.applyZoom(Math.min(availableWidth / size.width, availableHeight / size.height));
    },
    applyZoom(value) {
      const area = this.$refs.scrollArea;
      const nextZoom = Math.min(Math.max(Number(value) || 1, 0.25), 3);
      if (Math.abs(nextZoom - this.zoom) < 0.001) return;

      const anchorPage = this.currentPage || this.pageFromScroll(area?.scrollTop || 0);
      const previousPageTop = this.sumPageHeights(1, anchorPage - 1);
      const previousPageHeight = this.pageHeights[anchorPage] || 820;
      const offsetRatio = area
        ? Math.min(Math.max((area.scrollTop - previousPageTop) / previousPageHeight, 0), 1)
        : 0;

      this.zoom = nextZoom;
      this.$nextTick(() => {
        const nextPageTop = this.sumPageHeights(1, anchorPage - 1);
        const nextPageHeight = this.pageHeights[anchorPage] || 820;
        if (this.$refs.scrollArea) {
          this.$refs.scrollArea.scrollTop = Math.max(0, nextPageTop + nextPageHeight * offsetRatio);
        }
        this.updateRenderWindowAround(anchorPage);
        this.updateCurrentPage();
      });
    },
    rotatePage() {
      this.rotation = (this.rotation + 90) % 360;
      this.renderedThumbs = new Set();
    },
    onKeydown(event) {
      if (event.ctrlKey && ["s", "p"].includes(String(event.key).toLowerCase())) {
        event.preventDefault();
        event.stopPropagation();
        return;
      }
      if (event.target && ["INPUT", "TEXTAREA"].includes(event.target.tagName)) return;
      if (event.ctrlKey && (event.key === "+" || event.key === "=")) {
        event.preventDefault();
        this.setZoom(this.zoom + 0.15);
      }
      if (event.ctrlKey && event.key === "-") {
        event.preventDefault();
        this.setZoom(this.zoom - 0.15);
      }
      if (event.key === "PageUp") {
        event.preventDefault();
        this.goToPage(this.currentPage - 1);
      }
      if (event.key === "PageDown") {
        event.preventDefault();
        this.goToPage(this.currentPage + 1);
      }
      if (event.key === "ArrowLeft" || event.key === "ArrowUp") this.goToPage(this.currentPage - 1);
      if (event.key === "ArrowRight" || event.key === "ArrowDown") this.goToPage(this.currentPage + 1);
      if (event.key === "Escape" && this.isFullscreen) this.exitFullscreen();
    },
    async toggleFullscreen() {
      if (this.isFullscreen) return this.exitFullscreen();
      if (this.$el.requestFullscreen) await this.$el.requestFullscreen();
      this.isFullscreen = true;
      document.body.classList.add("pdf-reader-fullscreen");
    },
    async exitFullscreen() {
      if (document.fullscreenElement && document.exitFullscreen) await document.exitFullscreen();
      this.exitFullscreenClass();
    },
    exitFullscreenClass() {
      this.isFullscreen = false;
      document.body.classList.remove("pdf-reader-fullscreen");
    },
    onFullscreenChange() {
      if (!document.fullscreenElement) this.exitFullscreenClass();
    },
    getPoint(event) {
      const svg = event.currentTarget;
      const rect = svg.getBoundingClientRect();
      return {
        x: Math.min(Math.max((event.clientX - rect.left) / rect.width, 0), 1),
        y: Math.min(Math.max((event.clientY - rect.top) / rect.height, 0), 1),
      };
    },
    onAnnotationPointerDown(event, page) {
      if (this.readOnly || this.activeTool === "pan") return;
      event.preventDefault();
      const point = this.getPoint(event);
      if (this.activeTool === "text") {
        const text = window.prompt("Text");
        if (text) this.addAnnotation(page, { type: "text", x: point.x, y: point.y, text, color: this.strokeColor, size: 0.035 });
        return;
      }
      if (this.activeTool === "eraser") {
        this.eraseAt(page, point);
        return;
      }
      const base = { id: this.makeId(), color: this.strokeColor };
      if (this.activeTool === "pen") this.drawing = { page, item: { ...base, type: "pen", points: [point], width: 3, opacity: 1 } };
      if (this.activeTool === "highlighter") this.drawing = { page, item: { ...base, type: "highlighter", points: [point], width: 12, opacity: 0.35, color: "#f5d90a" } };
      if (this.activeTool === "rectangle" || this.activeTool === "circle" || this.activeTool === "arrow") {
        this.drawing = { page, item: { ...base, type: this.activeTool, start: point, end: point } };
      }
      if (this.drawing) {
        this.pushUndo(page);
        this.annotationsByPage[page] = [...(this.annotationsByPage[page] || []), this.drawing.item];
      }
    },
    onAnnotationPointerMove(event) {
      if (!this.drawing) return;
      const point = this.getPoint(event);
      const item = this.drawing.item;
      if (item.points) item.points.push(point);
      else item.end = point;
      this.markDirty(this.drawing.page);
    },
    onAnnotationPointerUp() {
      if (!this.drawing) return;
      this.markDirty(this.drawing.page);
      this.drawing = null;
    },
    addAnnotation(page, item) {
      this.pushUndo(page);
      this.annotationsByPage[page] = [...(this.annotationsByPage[page] || []), { id: this.makeId(), ...item }];
      this.markDirty(page);
    },
    eraseAt(page, point) {
      const list = this.annotationsByPage[page] || [];
      const index = list.findIndex((item) => this.annotationNearPoint(item, point));
      if (index >= 0) {
        this.pushUndo(page);
        this.annotationsByPage[page] = list.filter((_, i) => i !== index);
        this.markDirty(page);
      }
    },
    annotationNearPoint(item, point) {
      const threshold = 0.025;
      if (item.points) return item.points.some((p) => Math.abs(p.x - point.x) < threshold && Math.abs(p.y - point.y) < threshold);
      if (item.start && item.end) {
        const box = this.rectFromItem(item);
        return point.x >= box.x - threshold && point.x <= box.x + box.width + threshold && point.y >= box.y - threshold && point.y <= box.y + box.height + threshold;
      }
      return Math.abs(item.x - point.x) < threshold && Math.abs(item.y - point.y) < threshold;
    },
    pushUndo(page) {
      this.undoStack[page] = this.undoStack[page] || [];
      this.redoStack[page] = [];
      this.undoStack[page].push(JSON.stringify(this.annotationsByPage[page] || []));
    },
    undo() {
      const page = this.currentPage;
      const stack = this.undoStack[page] || [];
      if (!stack.length) return;
      this.redoStack[page] = this.redoStack[page] || [];
      this.redoStack[page].push(JSON.stringify(this.annotationsByPage[page] || []));
      this.annotationsByPage[page] = JSON.parse(stack.pop());
      this.markDirty(page);
    },
    redo() {
      const page = this.currentPage;
      const stack = this.redoStack[page] || [];
      if (!stack.length) return;
      this.pushUndo(page);
      this.annotationsByPage[page] = JSON.parse(stack.pop());
      this.markDirty(page);
    },
    clearPage() {
      this.pushUndo(this.currentPage);
      this.annotationsByPage[this.currentPage] = [];
      this.markDirty(this.currentPage);
    },
    markDirty(page) {
      this.dirtyPages.add(page);
      this.saveState = navigator.onLine ? "saving" : "offline";
      this.saveLocalBackup();
    },
    async loadAnnotations() {
      try {
        const response = await axios.get(`/pdf-reader/${this.contentId}/annotations`);
        this.annotationsByPage = response.data.data || {};
        const local = this.readLocalBackup();
        if (local) this.annotationsByPage = { ...this.annotationsByPage, ...local.annotationsByPage };
      } catch (e) {
        const local = this.readLocalBackup();
        if (local) this.annotationsByPage = local.annotationsByPage || {};
      }
    },
    async flushAnnotations() {
      if (this.readOnly || !this.dirtyPages.size) return;
      const pages = Array.from(this.dirtyPages).map((page) => ({
        page_number: Number(page),
        annotation_json: this.annotationsByPage[page] || [],
      }));
      try {
        await axios.post(`/pdf-reader/${this.contentId}/annotations`, { pages });
        this.dirtyPages.clear();
        localStorage.removeItem(this.offlineKey);
        this.saveState = "saved";
      } catch (e) {
        this.queueOffline(pages);
        this.saveState = navigator.onLine ? "error" : "offline";
      }
    },
    queueOffline(pages) {
      const queued = JSON.parse(localStorage.getItem(`${this.offlineKey}-queue`) || "[]");
      localStorage.setItem(`${this.offlineKey}-queue`, JSON.stringify([...queued, ...pages]));
      this.saveLocalBackup();
    },
    async flushOfflineQueue() {
      const key = `${this.offlineKey}-queue`;
      const queued = JSON.parse(localStorage.getItem(key) || "[]");
      if (!queued.length) return;
      const byPage = {};
      queued.forEach((page) => { byPage[page.page_number] = page; });
      try {
        await axios.post(`/pdf-reader/${this.contentId}/annotations`, { pages: Object.values(byPage) });
        localStorage.removeItem(key);
        this.saveState = "saved";
      } catch (e) {
        this.saveState = "error";
      }
    },
    saveLocalBackup() {
      localStorage.setItem(this.offlineKey, JSON.stringify({
        annotationsByPage: this.annotationsByPage,
        updatedAt: Date.now(),
      }));
    },
    readLocalBackup() {
      try {
        return JSON.parse(localStorage.getItem(this.offlineKey));
      } catch (e) {
        return null;
      }
    },
    async loadProgress() {
      try {
        this.loadingProgress = true;
        const response = await axios.get(`/pdf-reader/${this.contentId}/progress`);
        const progress = response.data.data || {};
        this.zoom = Number(progress.zoom || 1);
        this.lastSavedProgress = this.normalizeProgress({
          last_page: Number(progress.last_page || 1),
          zoom: Number(progress.zoom || 1),
          scroll_position: Number(progress.scroll_position || 0),
        });
        this.scrollRestore = {
          lastPage: Number(progress.last_page || 1),
          scrollPosition: Number(progress.scroll_position || 0),
        };
      } catch (e) {
        this.scrollRestore = null;
      } finally {
        this.loadingProgress = false;
      }
    },
    restoreScrollPosition() {
      if (!this.scrollRestore) return;
      this.loadingProgress = true;
      if (this.scrollRestore.scrollPosition > 0) {
        this.updateRenderWindowAround(this.pageFromScroll(this.scrollRestore.scrollPosition));
        this.$refs.scrollArea.scrollTop = this.scrollRestore.scrollPosition;
      } else {
        this.goToPage(this.scrollRestore.lastPage);
      }
      setTimeout(() => {
        this.loadingProgress = false;
        this.progressDirty = false;
      }, 700);
    },
    queueProgressSave() {
      clearTimeout(this._progressTimeout);
      this._progressTimeout = setTimeout(this.saveProgress, 900);
    },
    markProgressDirty() {
      if (!this.pdf || this.loadingProgress) return;
      this.progressDirty = true;
      this.queueProgressSave();
    },
    normalizeProgress(progress) {
      return {
        last_page: Number(progress.last_page || 1),
        zoom: Number(Number(progress.zoom || 1).toFixed(3)),
        scroll_position: Number(progress.scroll_position || 0),
      };
    },
    currentProgressPayload() {
      return this.normalizeProgress({
        last_page: this.currentPage,
        zoom: this.zoom,
        scroll_position: Math.max(0, Math.round(this.$refs.scrollArea?.scrollTop || 0)),
      });
    },
    async saveProgress(force = false) {
      if (!this.pdf || !this.$refs.scrollArea) return;
      const payload = this.currentProgressPayload();
      if (!force && !this.progressDirty) return;
      if (this.lastSavedProgress && JSON.stringify(payload) === JSON.stringify(this.lastSavedProgress)) {
        this.progressDirty = false;
        return;
      }
      try {
        await axios.post(`/pdf-reader/${this.contentId}/progress`, payload);
        this.lastSavedProgress = payload;
        this.progressDirty = false;
      } catch (e) {
        localStorage.setItem(`pdf-reader-progress-${this.contentId}`, JSON.stringify(payload));
      }
    },
    async runSearch() {
      if (!this.searchQuery || !this.pdf) return;
      this.searchResults = [];
      this.searchHighlights = {};
      const query = this.searchQuery.toLowerCase();
      for (let pageNumber = 1; pageNumber <= this.totalPages; pageNumber++) {
        const page = await this.pdf.getPage(pageNumber);
        const viewport = page.getViewport({ scale: 1, rotation: this.rotation });
        const text = await page.getTextContent();
        const combinedText = text.items.map((item) => String(item.str || "")).join(" ").toLowerCase();
        let pageMatched = false;
        text.items.forEach((item) => {
          const value = String(item.str || "");
          if (!value.toLowerCase().includes(query)) return;
          const tx = pdfjsLib.Util.transform(viewport.transform, item.transform);
          const x = tx[4] / viewport.width;
          const y = 1 - (tx[5] / viewport.height);
          const width = Math.min(Math.max((item.width || value.length * 6) / viewport.width, 0.03), 0.9);
          const height = Math.min(Math.max((item.height || 12) / viewport.height, 0.012), 0.06);
          this.searchHighlights[pageNumber] = [...(this.searchHighlights[pageNumber] || []), { x, y: y - height, width, height }];
          pageMatched = true;
        });
        if (!pageMatched && combinedText.includes(query)) {
          this.searchHighlights[pageNumber] = [{ x: 0.08, y: 0.08, width: 0.84, height: 0.055 }];
          pageMatched = true;
        }
        if (pageMatched) this.searchResults.push({ page: pageNumber });
        if (pageNumber % 20 === 0) await new Promise((resolve) => setTimeout(resolve, 0));
      }
      this.activeSearchIndex = this.searchResults.length ? 0 : -1;
      if (this.activeSearchIndex >= 0) this.goToPage(this.searchResults[0].page);
    },
    pointsToString(points) {
      return (points || []).map((point) => `${point.x},${point.y}`).join(" ");
    },
    rectFromItem(item) {
      const x = Math.min(item.start.x, item.end.x);
      const y = Math.min(item.start.y, item.end.y);
      return { x, y, width: Math.abs(item.end.x - item.start.x), height: Math.abs(item.end.y - item.start.y) };
    },
    ellipseFromItem(item) {
      const rect = this.rectFromItem(item);
      return { cx: rect.x + rect.width / 2, cy: rect.y + rect.height / 2, rx: rect.width / 2, ry: rect.height / 2 };
    },
    arrowHead(item) {
      const angle = Math.atan2(item.end.y - item.start.y, item.end.x - item.start.x);
      const size = 0.025;
      const a1 = angle + Math.PI * 0.82;
      const a2 = angle - Math.PI * 0.82;
      return `${item.end.x},${item.end.y} ${item.end.x + Math.cos(a1) * size},${item.end.y + Math.sin(a1) * size} ${item.end.x + Math.cos(a2) * size},${item.end.y + Math.sin(a2) * size}`;
    },
    makeId() {
      return `a-${Date.now()}-${Math.random().toString(16).slice(2)}`;
    },
  },
};
</script>

<style scoped>
.pdf-learning-viewer {
  --toolbar: #202124;
  --panel: #2f3136;
  --paper: #fff;
  --line: #d8dde6;
  display: flex;
  flex-direction: column;
  width: 100%;
  max-width: 100%;
  min-width: 0;
  height: min(82vh, 920px);
  min-height: 620px;
  background: #3b3d42;
  border-radius: 12px;
  overflow: hidden;
  color: #f8fafc;
  direction: ltr;
}
.pdf-learning-viewer.fullscreen {
  position: fixed;
  inset: 0;
  z-index: 99999;
  height: 100vh;
  min-height: 100vh;
  border-radius: 0;
}
.pdf-toolbar {
  flex: 0 0 auto;
  background: var(--toolbar);
  border-bottom: 1px solid rgba(255,255,255,.08);
  min-width: 0;
}
.toolbar-row {
  display: flex;
  align-items: center;
  gap: 8px;
  min-height: 48px;
  padding: 6px 10px;
}
.secondary-row {
  border-top: 1px solid rgba(255,255,255,.06);
  flex-wrap: wrap;
}
.toolbar-spacer { flex: 1; }
button {
  font-family: inherit;
}
.icon-btn,
.text-btn,
.mini-btn,
.annotation-tools button,
.thumb-toggle,
.floating-thumbs {
  border: 1px solid rgba(255,255,255,.12);
  background: rgba(255,255,255,.08);
  color: #fff;
  border-radius: 7px;
  min-width: 36px;
  height: 36px;
  cursor: pointer;
}
.icon-btn:disabled {
  opacity: .35;
  cursor: not-allowed;
}
.text-btn {
  padding: 0 10px;
  width: auto;
}
.page-control {
  display: flex;
  align-items: center;
  gap: 6px;
  color: #dfe3ea;
}
.page-control input {
  width: 64px;
  height: 32px;
  border-radius: 6px;
  border: 1px solid rgba(255,255,255,.18);
  background: #111827;
  color: #fff;
  text-align: center;
}
.zoom-label,
.save-state {
  min-width: 54px;
  font-size: 13px;
  color: #dfe3ea;
}
.save-state.offline,
.save-state.error {
  color: #fbbf24;
}
.search-box {
  display: grid;
  grid-template-columns: auto minmax(0, 1fr) auto auto;
  align-items: center;
  gap: 6px;
  min-height: 36px;
  padding: 0 8px;
  background: #111827;
  border-radius: 7px;
  border: 1px solid rgba(255,255,255,.12);
}
.search-box input {
  width: 170px;
  border: 0;
  outline: 0;
  background: transparent;
  color: #fff;
}
.mini-btn {
  min-width: 34px;
  height: 26px;
}
.pdf-body {
  display: grid;
  grid-template-columns: 128px 1fr 48px;
  position: relative;
  min-height: 0;
  min-width: 0;
  flex: 1;
}
.thumbs-panel {
  background: #25272c;
  border-right: 1px solid rgba(255,255,255,.08);
  overflow-y: auto;
  padding: 10px 8px;
}
.thumb {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  padding: 7px;
  margin-bottom: 8px;
  border-radius: 8px;
  cursor: pointer;
  border: 1px solid transparent;
  color: #cbd5e1;
}
.thumb.active {
  border-color: #60a5fa;
  background: rgba(96,165,250,.16);
}
.thumb canvas {
  width: 82px;
  min-height: 106px;
  background: #fff;
  border-radius: 3px;
}
.pdf-scroll {
  position: relative;
  overflow: auto;
  background: #4a4d54;
  min-width: 0;
}
.page-shell {
  display: flex;
  justify-content: center;
  padding: 12px;
}
.virtual-spacer {
  flex: 0 0 auto;
}
.page-frame {
  position: relative;
  flex: 0 0 auto;
  background: var(--paper);
  box-shadow: 0 8px 28px rgba(0,0,0,.28);
}
.pdf-canvas,
.annotation-layer {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
}
.annotation-layer {
  touch-action: pan-y pinch-zoom;
}
.watermark-layer {
  color: rgba(20, 30, 45, .13);
  font-size: .026px;
  font-weight: 700;
  letter-spacing: 0;
  pointer-events: none;
  user-select: none;
}
.annotation-layer .search-highlight {
  fill: rgba(255, 230, 0, .42);
}
.annotation-highlighter {
  mix-blend-mode: multiply;
}
.page-loading,
.viewer-loading,
.viewer-error {
  position: absolute;
  inset: auto 16px 16px auto;
  background: rgba(17,24,39,.88);
  border-radius: 8px;
  padding: 8px 12px;
  color: #fff;
  font-size: 13px;
}
.viewer-loading,
.viewer-error {
  inset: 20px auto auto 50%;
  transform: translateX(-50%);
  z-index: 2;
}
.annotation-tools {
  background: #25272c;
  border-left: 1px solid rgba(255,255,255,.08);
  padding: 8px 6px;
}
.tool-list {
  display: flex;
  flex-direction: column;
  gap: 7px;
}
.annotation-tools button.active {
  background: #2563eb;
  border-color: #60a5fa;
}
.color-input {
  width: 36px;
  height: 36px;
  padding: 2px;
  border: 0;
  border-radius: 7px;
  background: transparent;
}
.mobile-only {
  display: none;
}
:global(body.pdf-reader-fullscreen) {
  overflow: hidden;
}
:global(body.pdf-reader-fullscreen nav),
:global(body.pdf-reader-fullscreen footer),
:global(body.pdf-reader-fullscreen .bottom-nav),
:global(body.pdf-reader-fullscreen .floating-whatsapp),
:global(body.pdf-reader-fullscreen .contents-hero),
:global(body.pdf-reader-fullscreen .branches-strip),
:global(body.pdf-reader-fullscreen .contents-sidebar),
:global(body.pdf-reader-fullscreen .viewer-header) {
  display: none !important;
}
@media (max-width: 768px) {
  .pdf-learning-viewer {
    height: 76vh;
    min-height: 520px;
    border-radius: 10px;
  }
  .pdf-learning-viewer.fullscreen {
    height: 100vh;
  }
  .mobile-only {
    display: inline-flex;
    align-items: center;
    justify-content: center;
  }
  .pdf-toolbar.collapsed .secondary-row {
    display: none;
  }
  .secondary-row {
    display: grid;
    grid-template-columns: repeat(4, minmax(0, 1fr));
    align-items: center;
  }
  .secondary-row .search-box {
    grid-column: 1 / -1;
  }
  .toolbar-row {
    min-height: 44px;
    padding: 5px 7px;
    gap: 4px;
  }
  .main-row {
    display: grid;
    grid-template-columns: 32px 32px minmax(64px, 1fr) 32px minmax(32px, auto) 32px;
  }
  .main-row .toolbar-spacer {
    display: none;
  }
  .main-row .save-state {
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  .secondary-row .icon-btn,
  .secondary-row .text-btn,
  .main-row .icon-btn {
    width: 100%;
    min-width: 0;
    padding: 0 4px;
    font-size: 11px;
    height: 32px;
  }
  .main-row .page-control input {
    width: 48px;
    height: 30px;
  }
  .search-box {
    width: 100%;
  }
  .search-box input {
    min-width: 0;
    width: 100%;
  }
  .search-box .mini-btn {
    flex: 0 0 auto;
  }
  .pdf-body {
    grid-template-columns: 1fr;
  }
  .pdf-scroll {
    padding-bottom: 76px;
  }
  .thumbs-panel {
    display: none;
    position: absolute;
    inset: 0 auto 0 0;
    width: 116px;
    z-index: 10020;
    transform: none;
  }
  .thumbs-panel.open {
    display: block;
  }
  .annotation-tools {
    position: fixed;
    left: 10px;
    right: 10px;
    bottom: var(--pdf-mobile-safe-bottom, calc(78px + env(safe-area-inset-bottom, 0px)));
    z-index: 10025;
    padding: 0;
    border: 0;
    background: transparent;
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    pointer-events: none;
  }
  .tool-list {
    display: grid;
    grid-template-columns: repeat(4, 44px);
    gap: 8px;
    max-width: min(calc(100vw - 20px), 232px);
    max-height: var(--pdf-mobile-tool-sheet-max-height, 220px);
    overflow: visible;
    padding: 10px;
    margin-bottom: 10px;
    border-radius: 14px;
    background: #25272c;
    box-shadow: 0 10px 30px rgba(0,0,0,.3);
    border: 1px solid rgba(255,255,255,.1);
    opacity: 0;
    transform: translateY(12px) scale(.96);
    transform-origin: bottom right;
    pointer-events: none;
    transition: opacity .16s ease, transform .16s ease;
  }
  .annotation-tools.open .tool-list {
    opacity: 1;
    transform: translateY(0) scale(1);
    pointer-events: auto;
  }
  .annotation-tools .tool-list button,
  .annotation-tools .color-input {
    width: 44px;
    height: 44px;
    min-width: 44px;
  }
  .annotation-tools .tool-toggle {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 52px;
    height: 52px;
    border-radius: 999px;
    background: #2563eb;
    color: #fff;
    border-color: rgba(255,255,255,.22);
    box-shadow: 0 12px 28px rgba(37,99,235,.38);
    overflow: visible;
    pointer-events: auto;
  }
  .annotation-tools .tool-toggle .pi {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    color: #fff;
    font-size: 20px;
    line-height: 1;
    opacity: 1;
    width: 1em;
    height: 1em;
  }
  .floating-thumbs {
    position: sticky;
    top: 10px;
    left: 10px;
    z-index: 15;
    width: 42px;
  }
  .page-shell {
    padding: 8px;
  }
}
@media (max-width: 768px) and (orientation: landscape) {
  .pdf-learning-viewer {
    height: 100vh;
    min-height: 340px;
  }
  .annotation-tools {
    left: auto;
    width: min(232px, 48vw);
  }
  .tool-list {
    grid-template-columns: repeat(4, 40px);
    max-width: min(calc(100vw - 20px), 216px);
    max-height: var(--pdf-mobile-tool-sheet-max-height, 176px);
  }
  .annotation-tools .tool-list button,
  .annotation-tools .color-input {
    width: 40px;
    height: 40px;
    min-width: 40px;
  }
}
</style>
